import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Chip,
  InputBase,
  alpha,
  LinearProgress,
} from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import StarIcon from '@mui/icons-material/Star';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import { useChat } from '../common/ChatContext';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { motion } from 'framer-motion';
import { getToken, formatProblemTitle, resolveProfileImage, syncSessionUser } from '../common/utils';
import proxy from '../api/axios';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { updateUserInfo, logout } from '../redux/userSlice';
import { showAuth } from '../redux/authSlice';
import ProfileAvatarEditor from '../components/ProfileAvatarEditor';
import SolveActivityHeatmap from '../components/SolveActivityHeatmap';
import { timeAgo } from '../components/problems/discussion/discussionUtils';
import PerfectScrollbar from 'react-perfect-scrollbar';

const ACCENT = '#137274';
const PINK = '#f50057';

const difficultyMeta = {
  easy: { label: 'Easy', color: '#1a7f37' },
  medium: { label: 'Medium', color: '#d97706' },
  hard: { label: 'Hard', color: '#cf222e' },
};

const toPascalCase = (str) =>
  String(str || '')
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

const UserProfilePage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { username } = useParams();
  const isOwnProfile = !username;
  const { openConversationWithUser } = useChat();
  const [messaging, setMessaging] = useState(false);

  const handleMessageUser = async () => {
    if (!user?._id || messaging) return;
    setMessaging(true);
    try {
      await openConversationWithUser(user._id);
    } finally {
      setMessaging(false);
    }
  };

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [name, setName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [problemSearch, setProblemSearch] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [saving, setSaving] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [heatmapYear, setHeatmapYear] = useState(null);
  const [yearActivity, setYearActivity] = useState(null);
  const [yearActivityLoading, setYearActivityLoading] = useState(false);

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.1)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark
    ? '#0f1419'
    : 'linear-gradient(180deg, #eef6f8 0%, #f7f9fb 40%, #ffffff 100%)';

  const fetchUserDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      const endpoint = username ? `/users/${username}` : '/users';
      const { data } = await proxy.get(endpoint, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!data.user || typeof data.user !== 'object') {
        toast.error('No user exists with this username');
        navigate('/');
        return;
      }
      setUser(data.user);
      setName(data.user.name || '');
    } catch (err) {
      setError('Failed to load user data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const isSignedOutOwnProfile = isOwnProfile && !getToken();

  useEffect(() => {
    if (isSignedOutOwnProfile) {
      setLoading(false);
      setUser(null);
      setError('');
      return;
    }
    fetchUserDetails();
    setHeatmapYear(null);
    setYearActivity(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const handleHeatmapYearChange = async (nextYear) => {
    setHeatmapYear(nextYear);
    if (!nextYear || !user?.username) {
      setYearActivity(null);
      return;
    }
    setYearActivityLoading(true);
    try {
      const { data } = await proxy.get(
        `/users/${user.username}/solve-activity`,
        { params: { year: nextYear } },
      );
      setYearActivity(data.solveActivity);
    } catch (err) {
      toast.error('Failed to load that year\'s activity.');
      setHeatmapYear(null);
    } finally {
      setYearActivityLoading(false);
    }
  };

  const handleEditProfile = async () => {
    const changingPassword =
      showPasswordFields && Boolean(currentPassword || newPassword || confirmPassword);

    if (!name) {
      toast.error('Enter your display name');
      return;
    }
    if (changingPassword && (!newPassword || !currentPassword)) {
      toast.error('Enter both current and new passwords');
      return;
    }
    if (changingPassword && newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    if (changingPassword && newPassword?.length < 8) {
      toast.info('Please enter a strong password minimum of 8 characters');
      return;
    }

    setSaving(true);
    try {
      const token = getToken();
      const payload = {
        name,
        ...(changingPassword ? { currentPassword, newPassword } : {}),
      };

      await proxy.put(`/users/profile/${user._id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Profile updated successfully');
      const next = syncSessionUser({ name });
      dispatch(updateUserInfo(next));
      setEditModalOpen(false);
      setShowPasswordFields(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      fetchUserDetails();
    } catch (err) {
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error('Enter your password to confirm.');
      return;
    }
    setDeleting(true);
    try {
      const token = getToken();
      await proxy.delete('/users/profile/me', {
        data: { password: deletePassword },
        headers: { Authorization: `Bearer ${token}` },
      });
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      dispatch(logout());
      toast.success('Your account has been deleted.');
      navigate('/');
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete account. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const solved = user?.solvedProblems || [];

  const difficultyStats = useMemo(() => {
    const counts = { easy: 0, medium: 0, hard: 0 };
    solved.forEach((p) => {
      const d = String(p?.difficulty || '').toLowerCase();
      if (counts[d] !== undefined) counts[d] += 1;
    });
    const total = solved.length || 1;
    return {
      counts,
      percents: {
        easy: Math.round((counts.easy / total) * 100),
        medium: Math.round((counts.medium / total) * 100),
        hard: Math.round((counts.hard / total) * 100),
      },
    };
  }, [solved]);

  const topTopics = useMemo(() => {
    const map = {};
    solved.forEach((p) => {
      (p.topics || []).forEach((t) => {
        const key = String(t).toLowerCase();
        map[key] = (map[key] || 0) + 1;
      });
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([topic, count]) => ({ topic, count }));
  }, [solved]);

  const filteredSolved = useMemo(() => {
    const q = problemSearch.trim().toLowerCase();
    return solved.filter((p) => {
      const matchesSearch =
        !q ||
        (p.title || '').toLowerCase().includes(q) ||
        (p.slug || '').toLowerCase().includes(q);
      const d = String(p.difficulty || '').toLowerCase();
      const matchesDiff = difficultyFilter === 'all' || d === difficultyFilter;
      return matchesSearch && matchesDiff;
    });
  }, [solved, problemSearch, difficultyFilter]);

  const initials = (user?.name || user?.username || '?')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1.5,
          background: pageBg,
        }}
      >
        <CircularProgress sx={{ color: ACCENT }} />
        <Typography sx={{ color: muted, fontWeight: 600 }}>Loading profile…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 640, mx: 'auto' }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
        <Button
          onClick={fetchUserDetails}
          sx={{ mt: 2, textTransform: 'none', fontWeight: 700, color: ACCENT }}
        >
          Try again
        </Button>
      </Box>
    );
  }

  if (isSignedOutOwnProfile) {
    return (
      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          gap: 2,
          px: 3,
          background: pageBg,
        }}
      >
        <Helmet>
          <title>Sign in required | CrafterHack</title>
        </Helmet>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(ACCENT, isDark ? 0.2 : 0.1),
            color: ACCENT,
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 30 }} />
        </Box>
        <Typography sx={{ fontWeight: 900, fontSize: '1.4rem', letterSpacing: '-0.02em' }}>
          Sign in to view your profile
        </Typography>
        <Typography variant="body2" sx={{ color: muted, maxWidth: 380 }}>
          Log in to see your solved problems, points, and progress on
          CrafterHack.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, mt: 1 }}>
          <Button
            variant="contained"
            onClick={() => dispatch(showAuth())}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 2,
              px: 3,
              bgcolor: ACCENT,
              boxShadow: `0 8px 20px ${alpha(ACCENT, 0.28)}`,
              '&:hover': { bgcolor: '#0f5c5e' },
            }}
          >
            Sign in
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/')}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
              borderColor: border,
              color: 'inherit',
            }}
          >
            Browse problems
          </Button>
        </Box>
      </Box>
    );
  }

  if (!user) return null;

  return (
    <Box sx={{ minHeight: '100vh', background: pageBg }}>
      <Helmet>
        <title>
          {user.name} (@{user.username}) | Profile | CrafterHack
        </title>
        <meta
          name="description"
          content={`View ${user.name}'s CrafterHack profile — ${solved.length} problems solved, ${user.points || 0} points.`}
        />
      </Helmet>

      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          px: { xs: 2, sm: 3 },
          pt: { xs: 3.5, md: 5 },
          pb: { xs: 3, md: 4 },
          borderBottom: `1px solid ${border}`,
          background: isDark
            ? `radial-gradient(900px 420px at 12% -20%, ${alpha(ACCENT, 0.35)}, transparent 55%), radial-gradient(700px 320px at 90% 0%, ${alpha(PINK, 0.14)}, transparent 50%), #0f1419`
            : `radial-gradient(900px 420px at 10% -25%, ${alpha(ACCENT, 0.2)}, transparent 55%), radial-gradient(700px 320px at 95% 0%, ${alpha(PINK, 0.08)}, transparent 50%), linear-gradient(180deg, #eef7f8 0%, #f8fafb 100%)`,
        }}
      >
        <Box sx={{ maxWidth: 1040, mx: 'auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 2, sm: 3 },
              }}
            >
              {isOwnProfile ? (
                <ProfileAvatarEditor
                  name={user.name}
                  profileImage={user.profileImage}
                  initials={initials}
                  size={108}
                  isDark={isDark}
                  muted={muted}
                  onUpdated={(updated) =>
                    setUser((prev) => ({
                      ...prev,
                      ...updated,
                      solvedProblems: prev.solvedProblems,
                    }))
                  }
                />
              ) : (
                <Avatar
                  alt={user.name}
                  src={resolveProfileImage(user.profileImage)}
                  sx={{
                    width: { xs: 88, sm: 108 },
                    height: { xs: 88, sm: 108 },
                    fontSize: { xs: 32, sm: 40 },
                    fontWeight: 800,
                    bgcolor: alpha(ACCENT, isDark ? 0.35 : 0.18),
                    color: isDark ? '#7ed4d4' : ACCENT,
                    border: `3px solid ${isDark ? alpha('#fff', 0.12) : '#fff'}`,
                    boxShadow: `0 12px 32px ${alpha(ACCENT, 0.2)}`,
                  }}
                >
                  {initials}
                </Avatar>
              )}

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: '1.5rem', sm: '1.85rem' },
                      letterSpacing: '-0.03em',
                      lineHeight: 1.15,
                    }}
                  >
                    {user.name}
                  </Typography>
                  <Chip
                    size="small"
                    icon={
                      user.isPremiumUser ? (
                        <WorkspacePremiumOutlinedIcon sx={{ fontSize: '16px !important' }} />
                      ) : (
                        <StarIcon sx={{ fontSize: '16px !important' }} />
                      )
                    }
                    label={user.isPremiumUser ? 'Premium' : 'Standard'}
                    sx={{
                      fontWeight: 700,
                      height: 26,
                      bgcolor: user.isPremiumUser
                        ? alpha('#d97706', isDark ? 0.25 : 0.14)
                        : alpha(ACCENT, isDark ? 0.2 : 0.1),
                      color: user.isPremiumUser ? '#d97706' : ACCENT,
                      '& .MuiChip-icon': {
                        color: user.isPremiumUser ? '#d97706' : ACCENT,
                      },
                    }}
                  />
                </Box>
                <Typography sx={{ color: muted, fontWeight: 600, mb: isOwnProfile ? 1.5 : 0.5 }}>
                  @{user.username}
                </Typography>
                {!isOwnProfile && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: user.isOnline ? ACCENT : '#9ca3af',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: user.isOnline ? ACCENT : muted, fontWeight: 600 }}
                    >
                      {user.isOnline
                        ? 'Online'
                        : user.lastSeenAt
                        ? `Last seen ${timeAgo(user.lastSeenAt)}`
                        : 'Offline'}
                    </Typography>
                  </Box>
                )}
                <Typography variant="body2" sx={{ color: muted, maxWidth: 480, lineHeight: 1.5 }}>
                  {isOwnProfile
                    ? 'Your coding journey on CrafterHack — track solves, points, and momentum.'
                    : `Exploring ${user.name?.split(' ')[0] || 'this coder'}'s progress on CrafterHack.`}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {isOwnProfile && (
                  <Button
                    variant="contained"
                    startIcon={<EditOutlinedIcon />}
                    onClick={() => setEditModalOpen(true)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      borderRadius: 2,
                      bgcolor: ACCENT,
                      boxShadow: `0 8px 20px ${alpha(ACCENT, 0.28)}`,
                      '&:hover': { bgcolor: '#0f5c5e' },
                    }}
                  >
                    Edit profile
                  </Button>
                )}
                {!isOwnProfile && getToken() && (
                  <Button
                    variant="contained"
                    startIcon={<ChatBubbleOutlineRoundedIcon />}
                    onClick={handleMessageUser}
                    disabled={messaging}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      borderRadius: 2,
                      bgcolor: ACCENT,
                      boxShadow: `0 8px 20px ${alpha(ACCENT, 0.28)}`,
                      '&:hover': { bgcolor: '#0f5c5e' },
                    }}
                  >
                    Message
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<LeaderboardOutlinedIcon />}
                  onClick={() => navigate('/leaderboard')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2,
                    borderColor: border,
                    color: 'inherit',
                    bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#fff', 0.55),
                  }}
                >
                  Leaderboard
                </Button>
              </Box>
            </Box>
          </motion.div>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1040, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, md: 4 } }}>
        {/* Stats */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr 1fr',
              sm: 'repeat(4, 1fr)',
            },
            gap: 1.5,
            mb: 2.5,
          }}
        >
          {[
            {
              label: 'Rank',
              value:
                user.rank != null && user.totalSolvers
                  ? `#${user.rank}`
                  : '—',
              sub:
                user.totalSolvers > 0
                  ? `of ${user.totalSolvers} solvers`
                  : 'No solvers yet',
              icon: <LeaderboardOutlinedIcon sx={{ fontSize: 20 }} />,
              tint: PINK,
              onClick: () => navigate('/leaderboard'),
            },
            {
              label: 'Points',
              value: user.points || 0,
              icon: <EmojiEventsOutlinedIcon sx={{ fontSize: 20 }} />,
              tint: '#d97706',
            },
            {
              label: 'Solved',
              value: solved.length,
              icon: <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />,
              tint: ACCENT,
            },
            {
              label: 'Active days',
              value: user.solveActivity?.activeDays ?? 0,
              sub: 'last 12 months',
              icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 20 }} />,
              tint: ACCENT,
            },
          ].map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * i, duration: 0.3 }}
            >
              <Paper
                elevation={0}
                onClick={s.onClick}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: `1px solid ${border}`,
                  bgcolor: surface,
                  height: '100%',
                  cursor: s.onClick ? 'pointer' : 'default',
                  transition: 'border-color 0.15s, transform 0.15s',
                  ...(s.onClick
                    ? {
                        '&:hover': {
                          borderColor: alpha(s.tint, 0.45),
                          transform: 'translateY(-1px)',
                        },
                      }
                    : null),
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.25,
                    bgcolor: alpha(s.tint, isDark ? 0.2 : 0.12),
                    color: s.tint,
                  }}
                >
                  {s.icon}
                </Box>
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: '1.45rem',
                    letterSpacing: '-0.03em',
                    lineHeight: 1,
                    mb: 0.5,
                  }}
                >
                  {s.value}
                </Typography>
                <Typography variant="caption" sx={{ color: muted, fontWeight: 700, display: 'block' }}>
                  {s.label}
                </Typography>
                {s.sub && (
                  <Typography
                    variant="caption"
                    sx={{ color: muted, fontWeight: 600, fontSize: '0.68rem' }}
                  >
                    {s.sub}
                  </Typography>
                )}
              </Paper>
            </motion.div>
          ))}
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <SolveActivityHeatmap
            activity={heatmapYear ? yearActivity : user.solveActivity}
            isDark={isDark}
            border={border}
            muted={muted}
            surface={surface}
            year={heatmapYear}
            availableYears={user.solveActivityYears}
            onYearChange={handleHeatmapYearChange}
            loading={yearActivityLoading}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2.5,
            alignItems: 'flex-start',
          }}
        >
          {/* Sidebar insights */}
          <Box
            sx={{
              width: { xs: '100%', md: 300 },
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2.25,
                borderRadius: 2.5,
                border: `1px solid ${border}`,
                bgcolor: surface,
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 1.75 }}>
                Difficulty mix
              </Typography>
              {solved.length === 0 ? (
                <Typography variant="body2" sx={{ color: muted }}>
                  No solves yet — difficulty breakdown will show up here.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {(['easy', 'medium', 'hard']).map((key) => {
                    const meta = difficultyMeta[key];
                    const count = difficultyStats.counts[key];
                    const pct =
                      solved.length > 0
                        ? Math.round((count / solved.length) * 100)
                        : 0;
                    return (
                      <Box key={key}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: meta.color, fontSize: '0.8rem' }}
                          >
                            {meta.label}
                          </Typography>
                          <Typography variant="caption" sx={{ color: muted, fontWeight: 700 }}>
                            {count} · {pct}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          sx={{
                            height: 8,
                            borderRadius: 99,
                            bgcolor: alpha(meta.color, isDark ? 0.15 : 0.1),
                            '& .MuiLinearProgress-bar': {
                              bgcolor: meta.color,
                              borderRadius: 99,
                            },
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 2.25,
                borderRadius: 2.5,
                border: `1px solid ${border}`,
                bgcolor: surface,
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 1.25 }}>
                Topics practiced
              </Typography>
              {topTopics.length === 0 ? (
                <Typography variant="body2" sx={{ color: muted }}>
                  Topics from solved problems will appear here.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {topTopics.map(({ topic, count }) => (
                    <Chip
                      key={topic}
                      size="small"
                      label={`${toPascalCase(topic)} · ${count}`}
                      sx={{
                        fontWeight: 650,
                        fontSize: '0.72rem',
                        bgcolor: alpha(ACCENT, isDark ? 0.18 : 0.08),
                        color: isDark ? '#9fd4d5' : ACCENT,
                        border: `1px solid ${alpha(ACCENT, 0.2)}`,
                      }}
                    />
                  ))}
                </Box>
              )}
            </Paper>

            {isOwnProfile && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.25,
                  borderRadius: 2.5,
                  border: `1px solid ${border}`,
                  background: isDark
                    ? `linear-gradient(135deg, ${alpha(ACCENT, 0.2)}, ${surface})`
                    : `linear-gradient(135deg, ${alpha(ACCENT, 0.1)}, #fff)`,
                }}
              >
                <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Keep the streak going</Typography>
                <Typography variant="body2" sx={{ color: muted, mb: 1.75, fontSize: '0.82rem' }}>
                  Jump into Practice Track or pick a fresh problem.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<RouteOutlinedIcon />}
                    onClick={() => navigate('/practice-track')}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      bgcolor: ACCENT,
                      '&:hover': { bgcolor: '#0f5c5e' },
                    }}
                  >
                    Practice Track
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/')}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderColor: border,
                      color: 'inherit',
                    }}
                  >
                    Browse problems
                  </Button>
                </Box>
              </Paper>
            )}
          </Box>

          {/* Solved list */}
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'flex-end' },
                justifyContent: 'space-between',
                gap: 1.25,
                mb: 1.75,
              }}
            >
              <Box>
                <Typography
                  sx={{ fontWeight: 800, fontSize: '1.2rem', letterSpacing: '-0.02em' }}
                >
                  Solved problems
                </Typography>
                <Typography variant="body2" sx={{ color: muted }}>
                  {filteredSolved.length} of {solved.length} shown
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {['all', 'easy', 'medium', 'hard'].map((d) => {
                  const active = difficultyFilter === d;
                  const color =
                    d === 'all' ? ACCENT : difficultyMeta[d]?.color || ACCENT;
                  return (
                    <Chip
                      key={d}
                      size="small"
                      clickable
                      label={d === 'all' ? 'All' : difficultyMeta[d].label}
                      onClick={() => setDifficultyFilter(d)}
                      sx={{
                        fontWeight: 700,
                        bgcolor: active ? color : 'transparent',
                        color: active ? '#fff' : 'inherit',
                        border: `1px solid ${active ? color : border}`,
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            <Paper
              elevation={0}
              sx={{
                mb: 1.5,
                p: 0.5,
                pl: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                borderRadius: 2.5,
                border: `1px solid ${border}`,
                bgcolor: surface,
              }}
            >
              <SearchIcon sx={{ color: muted, fontSize: 20 }} />
              <InputBase
                sx={{ flex: 1, fontSize: '0.9rem' }}
                placeholder="Search solved problems…"
                value={problemSearch}
                onChange={(e) => setProblemSearch(e.target.value)}
              />
            </Paper>

            {solved.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 6,
                  px: 2,
                  borderRadius: 2.5,
                  border: `1px dashed ${border}`,
                  bgcolor: surface,
                }}
              >
                <CheckCircleOutlineIcon sx={{ fontSize: 40, color: muted, mb: 1 }} />
                <Typography sx={{ fontWeight: 800, mb: 0.5 }}>No problems solved yet</Typography>
                <Typography variant="body2" sx={{ color: muted, mb: 2 }}>
                  {isOwnProfile
                    ? 'Start with an easy problem or open Practice Track.'
                    : 'This coder hasn’t logged any solves yet.'}
                </Typography>
                {isOwnProfile && (
                  <Button
                    variant="contained"
                    onClick={() => navigate('/')}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      bgcolor: ACCENT,
                      '&:hover': { bgcolor: '#0f5c5e' },
                    }}
                  >
                    Find a problem
                  </Button>
                )}
              </Box>
            ) : filteredSolved.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 5,
                  borderRadius: 2.5,
                  border: `1px dashed ${border}`,
                  bgcolor: surface,
                }}
              >
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>No matches</Typography>
                <Typography variant="body2" sx={{ color: muted }}>
                  Try another search or clear the difficulty filter.
                </Typography>
              </Box>
            ) : (
              <PerfectScrollbar
                style={{ maxHeight: 650 }}
                options={{ suppressScrollX: true, wheelPropagation: false }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    pr: 1,
                  }}
                >
                  {filteredSolved.map((problem, index) => {
                  const d = String(problem.difficulty || '').toLowerCase();
                  const meta = difficultyMeta[d] || {
                    label: toPascalCase(problem.difficulty || 'N/A'),
                    color: muted,
                  };
                  return (
                    <Box
                      key={problem._id || problem.slug || index}
                      component={Link}
                      to={`/problems/${problem.slug}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 1.75,
                        py: 1.35,
                        textDecoration: 'none',
                        color: 'inherit',
                        borderRadius: 2,
                        border: `1px solid ${border}`,
                        bgcolor: surface,
                        transition: 'border-color 0.15s, background 0.15s',
                        '&:hover': {
                          borderColor: alpha(ACCENT, 0.45),
                          bgcolor: isDark ? alpha('#fff', 0.03) : alpha(ACCENT, 0.04),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          bgcolor: alpha(ACCENT, isDark ? 0.18 : 0.1),
                          color: ACCENT,
                        }}
                      >
                        <CodeOutlinedIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 650,
                            fontSize: '0.92rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatProblemTitle(problem.title)}
                        </Typography>
                        {problem.topics?.[0] && (
                          <Typography variant="caption" sx={{ color: muted }}>
                            {toPascalCase(problem.topics[0])}
                            {problem.topics.length > 1
                              ? ` +${problem.topics.length - 1}`
                              : ''}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        size="small"
                        label={meta.label}
                        sx={{
                          height: 24,
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          flexShrink: 0,
                          bgcolor: alpha(meta.color, isDark ? 0.2 : 0.1),
                          color: meta.color,
                        }}
                      />
                    </Box>
                  );
                })}
                </Box>
              </PerfectScrollbar>
            )}
          </Box>
        </Box>
      </Box>

      {/* Edit dialog */}
      <Dialog
        open={editModalOpen}
        onClose={() => !saving && setEditModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${border}`,
            bgcolor: surface,
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1.5,
            fontWeight: 800,
            letterSpacing: '-0.02em',
          }}
        >
          Edit profile
          <IconButton
            onClick={() => setEditModalOpen(false)}
            disabled={saving}
            aria-label="Close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Typography variant="body2" sx={{ color: muted, mb: 2 }}>
            Update how your name appears. Password changes are optional.
          </Typography>
          <TextField
            label="Display name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: ACCENT,
              },
              '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
            }}
          />
          <Button
            startIcon={<LockOutlinedIcon />}
            onClick={() => setShowPasswordFields(!showPasswordFields)}
            sx={{
              mb: 2,
              textTransform: 'none',
              fontWeight: 700,
              color: ACCENT,
            }}
          >
            {showPasswordFields ? 'Hide password fields' : 'Change password'}
          </Button>
          {showPasswordFields && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              <TextField
                label="Current password"
                fullWidth
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <TextField
                label="New password"
                fullWidth
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                helperText="Minimum 8 characters"
              />
              <TextField
                label="Confirm new password"
                fullWidth
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Box>
          )}

          <Box
            sx={{
              mt: 3,
              pt: 2.5,
              borderTop: `1px solid ${border}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 800, color: '#cf222e', mb: 0.5 }}
            >
              Danger zone
            </Typography>
            <Typography variant="body2" sx={{ color: muted, mb: 1.5 }}>
              Deleting your account disables it permanently — you'll be signed
              out immediately and won't be able to log back in.
            </Typography>
            <Button
              startIcon={<DeleteOutlineIcon />}
              onClick={() => {
                setEditModalOpen(false);
                setDeleteModalOpen(true);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                color: '#cf222e',
                border: '1px solid #cf222e33',
                '&:hover': { bgcolor: '#cf222e14', border: '1px solid #cf222e' },
              }}
            >
              Delete account
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setEditModalOpen(false)}
            disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 700, color: muted }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditProfile}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
            variant="contained"
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: ACCENT,
              '&:hover': { bgcolor: '#0f5c5e' },
            }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete account confirmation */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => {
          if (deleting) return;
          setDeleteModalOpen(false);
          setDeletePassword('');
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${border}`,
            bgcolor: surface,
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            color: '#cf222e',
          }}
        >
          <WarningAmberOutlinedIcon />
          Delete your account?
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Typography variant="body2" sx={{ color: muted, mb: 2 }}>
            This immediately disables your account and signs you out. You
            won't be able to log back in. Enter your password to confirm.
          </Typography>
          <TextField
            label="Password"
            fullWidth
            type="password"
            autoFocus
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            disabled={deleting}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => {
              setDeleteModalOpen(false);
              setDeletePassword('');
            }}
            disabled={deleting}
            sx={{ textTransform: 'none', fontWeight: 700, color: muted }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            variant="contained"
            disabled={deleting}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: '#cf222e',
              '&:hover': { bgcolor: '#a91e2b' },
            }}
          >
            {deleting ? 'Deleting…' : 'Delete my account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserProfilePage;
